/**
 * The single place the UI asks "can this view do X?".
 *
 * Mirrors functions/src/services/viewEntitlements.ts — the server enforces,
 * this decides what to show. Before it, every gate carried its own copy of the
 * condition: ShareDialog had a hardcoded `pro || beta` whitelist written twice
 * in consecutive lines, PhotosPage resolved resolution from the USER tier
 * (wrong in a per-view model), and the `features.raw` / `features.sharing`
 * flags in config were read by nobody at all.
 */

import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "@/config/firebase";
import { useEffectiveUserId } from "./useEffectiveUserId";
import { useUserData } from "./useUserData";
import { usePlansConfigV3 } from "./usePlansConfigV3";
import type {
  PlanTierV3,
  PlansConfigV3,
  VideoResolutionV3,
  ViewLicense,
} from "@/types";

export interface ViewEntitlements {
  tier: PlanTierV3;
  maxResolution: VideoResolutionV3;
  raw: boolean;
  /** Share page, client gallery and the WhatsApp daily photo all ride on this. */
  sharing: boolean;
  creditsPerMonth: number;
  unlimitedCredits: boolean;
  /**
   * False when nothing granted these entitlements — no license and not beta.
   * Lets a caller tell "free tier" apart from "pre-V3 account, read the legacy
   * V2 limits instead".
   */
  licensed: boolean;
}

/**
 * What a view with no usable license gets: the poorest tier, never a
 * permissive default. A missing license means the onViewCreate trigger has not
 * landed yet (a few seconds) or something is broken — neither deserves 4K.
 */
export const FREE_FALLBACK: ViewEntitlements = {
  tier: "free",
  maxResolution: "1080p",
  raw: false,
  sharing: false,
  creditsPerMonth: 0,
  unlimitedCredits: false,
  licensed: false,
};

function fromConfig(tier: PlanTierV3, config: PlansConfigV3): ViewEntitlements {
  const plan = config.plans[tier];
  if (!plan) return FREE_FALLBACK;

  const creditsPerMonth = plan.perView.creditsPerMonth;
  return {
    tier,
    maxResolution: plan.perView.maxResolution,
    raw: plan.features.raw,
    sharing: plan.features.sharing,
    creditsPerMonth,
    unlimitedCredits: creditsPerMonth < 0,
    licensed: true,
  };
}

/**
 * Entitlements for every view of the current user, keyed by viewId, kept live.
 *
 * `get(viewId)` falls back to the free tier for an unknown view, so callers
 * never have to handle undefined.
 */
export function useViewEntitlements(): {
  get: (viewId: string | undefined) => ViewEntitlements;
  loading: boolean;
} {
  const { effectiveUid } = useEffectiveUserId();
  const { userData, loading: userLoading } = useUserData();
  const { plansConfig, loading: plansLoading } = usePlansConfigV3();
  const [licenses, setLicenses] = useState<Map<string, ViewLicense>>(new Map());
  const [licensesLoaded, setLicensesLoaded] = useState(false);

  useEffect(() => {
    if (!effectiveUid) {
      setLicenses(new Map());
      setLicensesLoaded(true);
      return;
    }

    setLicensesLoaded(false);
    const ref = collection(db, `users/${effectiveUid}/viewLicenses`);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setLicenses(new Map(snap.docs.map((d) => [d.id, d.data() as ViewLicense])));
        setLicensesLoaded(true);
      },
      () => setLicensesLoaded(true)
    );
    return () => unsubscribe();
  }, [effectiveUid]);

  // Beta accounts carry the tier on the user document; all their views are beta.
  const isBeta = userData?.planV3?.tier === "beta";

  const get = (viewId: string | undefined): ViewEntitlements => {
    if (!plansConfig) return FREE_FALLBACK;
    if (isBeta) return fromConfig("beta", plansConfig);
    if (!viewId) return FREE_FALLBACK;

    const license = licenses.get(viewId);
    if (!license || license.status === "expired") return FREE_FALLBACK;

    return fromConfig(license.tier, plansConfig);
  };

  return { get, loading: userLoading || plansLoading || !licensesLoaded };
}
